/**
 * US states (50 + DC) with Census Bureau region/division and LAND neighbors.
 *
 * Used by the Region Teams builder (src/utils/regionTeamBuilder.ts) to merge
 * small states with adjacent ones, and by location validation (PUT /me/location).
 *
 * Adjacency rules:
 *  - Land borders only. Four Corners point-touches (AZ–CO, UT–NM) and
 *    water-only borders (MI–MN, MI–IL, NY–RI) are NOT neighbors.
 *  - AK and HI have no land neighbors (empty). The builder falls back to their
 *    census division (Pacific: WA, OR, CA) — see PACIFIC_FALLBACK below.
 *  - DC neighbors MD and VA.
 *  - The table is symmetric (asserted by the backend geo test and tests/geoConstants.test.ts).
 *
 * Territories (PR, VI, GU, AS, MP) are accepted as US "state" input at signup
 * but are treated as international by the builder (grouped among themselves,
 * then into a "Territories" bucket). They are NOT in US_STATES.
 *
 * Mirrored from the backend (beta-backend src/constants/geo). Keep the codes in
 * sync with tests/fixtures/geo-codes.snapshot.json (copied from the backend).
 */

export type CensusRegion = "Northeast" | "Midwest" | "South" | "West";

export type CensusDivision =
  | "New England"
  | "Middle Atlantic"
  | "East North Central"
  | "West North Central"
  | "South Atlantic"
  | "East South Central"
  | "West South Central"
  | "Mountain"
  | "Pacific";

export interface UsState {
  code: string;
  name: string;
  censusRegion: CensusRegion;
  censusDivision: CensusDivision;
  neighbors: string[];
}

export const CENSUS_DIVISION_REGION: Record<CensusDivision, CensusRegion> = {
  "New England": "Northeast",
  "Middle Atlantic": "Northeast",
  "East North Central": "Midwest",
  "West North Central": "Midwest",
  "South Atlantic": "South",
  "East South Central": "South",
  "West South Central": "South",
  Mountain: "West",
  Pacific: "West",
};

const s = (
  code: string,
  name: string,
  censusDivision: CensusDivision,
  neighbors: string[]
): UsState => ({
  code,
  name,
  censusRegion: CENSUS_DIVISION_REGION[censusDivision],
  censusDivision,
  neighbors,
});

const STATE_LIST: UsState[] = [
  s("AL", "Alabama", "East South Central", ["FL", "GA", "MS", "TN"]),
  s("AK", "Alaska", "Pacific", []),
  s("AZ", "Arizona", "Mountain", ["CA", "NM", "NV", "UT"]),
  s("AR", "Arkansas", "West South Central", ["LA", "MO", "MS", "OK", "TN", "TX"]),
  s("CA", "California", "Pacific", ["AZ", "NV", "OR"]),
  s("CO", "Colorado", "Mountain", ["KS", "NE", "NM", "OK", "UT", "WY"]),
  s("CT", "Connecticut", "New England", ["MA", "NY", "RI"]),
  s("DE", "Delaware", "South Atlantic", ["MD", "NJ", "PA"]),
  s("DC", "District of Columbia", "South Atlantic", ["MD", "VA"]),
  s("FL", "Florida", "South Atlantic", ["AL", "GA"]),
  s("GA", "Georgia", "South Atlantic", ["AL", "FL", "NC", "SC", "TN"]),
  s("HI", "Hawaii", "Pacific", []),
  s("ID", "Idaho", "Mountain", ["MT", "NV", "OR", "UT", "WA", "WY"]),
  s("IL", "Illinois", "East North Central", ["IA", "IN", "KY", "MO", "WI"]),
  s("IN", "Indiana", "East North Central", ["IL", "KY", "MI", "OH"]),
  s("IA", "Iowa", "West North Central", ["IL", "MN", "MO", "NE", "SD", "WI"]),
  s("KS", "Kansas", "West North Central", ["CO", "MO", "NE", "OK"]),
  s("KY", "Kentucky", "East South Central", ["IL", "IN", "MO", "OH", "TN", "VA", "WV"]),
  s("LA", "Louisiana", "West South Central", ["AR", "MS", "TX"]),
  s("ME", "Maine", "New England", ["NH"]),
  s("MD", "Maryland", "South Atlantic", ["DC", "DE", "PA", "VA", "WV"]),
  s("MA", "Massachusetts", "New England", ["CT", "NH", "NY", "RI", "VT"]),
  s("MI", "Michigan", "East North Central", ["IN", "OH", "WI"]),
  s("MN", "Minnesota", "West North Central", ["IA", "ND", "SD", "WI"]),
  s("MS", "Mississippi", "East South Central", ["AL", "AR", "LA", "TN"]),
  s("MO", "Missouri", "West North Central", ["AR", "IA", "IL", "KS", "KY", "NE", "OK", "TN"]),
  s("MT", "Montana", "Mountain", ["ID", "ND", "SD", "WY"]),
  s("NE", "Nebraska", "West North Central", ["CO", "IA", "KS", "MO", "SD", "WY"]),
  s("NV", "Nevada", "Mountain", ["AZ", "CA", "ID", "OR", "UT"]),
  s("NH", "New Hampshire", "New England", ["MA", "ME", "VT"]),
  s("NJ", "New Jersey", "Middle Atlantic", ["DE", "NY", "PA"]),
  s("NM", "New Mexico", "Mountain", ["AZ", "CO", "OK", "TX"]),
  s("NY", "New York", "Middle Atlantic", ["CT", "MA", "NJ", "PA", "VT"]),
  s("NC", "North Carolina", "South Atlantic", ["GA", "SC", "TN", "VA"]),
  s("ND", "North Dakota", "West North Central", ["MN", "MT", "SD"]),
  s("OH", "Ohio", "East North Central", ["IN", "KY", "MI", "PA", "WV"]),
  s("OK", "Oklahoma", "West South Central", ["AR", "CO", "KS", "MO", "NM", "TX"]),
  s("OR", "Oregon", "Pacific", ["CA", "ID", "NV", "WA"]),
  s("PA", "Pennsylvania", "Middle Atlantic", ["DE", "MD", "NJ", "NY", "OH", "WV"]),
  s("RI", "Rhode Island", "New England", ["CT", "MA"]),
  s("SC", "South Carolina", "South Atlantic", ["GA", "NC"]),
  s("SD", "South Dakota", "West North Central", ["IA", "MN", "MT", "ND", "NE", "WY"]),
  s("TN", "Tennessee", "East South Central", ["AL", "AR", "GA", "KY", "MO", "MS", "NC", "VA"]),
  s("TX", "Texas", "West South Central", ["AR", "LA", "NM", "OK"]),
  s("UT", "Utah", "Mountain", ["AZ", "CO", "ID", "NV", "WY"]),
  s("VT", "Vermont", "New England", ["MA", "NH", "NY"]),
  s("VA", "Virginia", "South Atlantic", ["DC", "KY", "MD", "NC", "TN", "WV"]),
  s("WA", "Washington", "Pacific", ["ID", "OR"]),
  s("WV", "West Virginia", "South Atlantic", ["KY", "MD", "OH", "PA", "VA"]),
  s("WI", "Wisconsin", "East North Central", ["IA", "IL", "MI", "MN"]),
  s("WY", "Wyoming", "Mountain", ["CO", "ID", "MT", "NE", "SD", "UT"]),
];

export const US_STATES: Readonly<Record<string, UsState>> = Object.freeze(
  Object.fromEntries(STATE_LIST.map((st) => [st.code, st]))
);

export const US_STATE_CODES: readonly string[] = STATE_LIST.map((st) => st.code);

/** US territories: accepted as `state` with country "US", treated as international. */
export const US_TERRITORIES: Readonly<Record<string, string>> = Object.freeze({
  PR: "Puerto Rico",
  VI: "U.S. Virgin Islands",
  GU: "Guam",
  AS: "American Samoa",
  MP: "Northern Mariana Islands",
});

export const US_TERRITORY_CODES: readonly string[] = Object.keys(US_TERRITORIES);

/**
 * AK/HI have no land neighbors. For "nearest region" purposes (merging, sub
 * placement, contiguity warnings) they are treated as adjacent to the
 * continental Pacific states.
 */
export const PACIFIC_FALLBACK: Readonly<Record<string, string[]>> = Object.freeze({
  AK: ["WA", "OR", "CA"],
  HI: ["CA", "OR", "WA"],
});

export function isUsState(code: unknown): boolean {
  return typeof code === "string" && Object.prototype.hasOwnProperty.call(US_STATES, code);
}

export function isUsTerritory(code: unknown): boolean {
  return typeof code === "string" && Object.prototype.hasOwnProperty.call(US_TERRITORIES, code);
}

/** Display name for a state or territory code ("OH" → "Ohio"); falls back to the code. */
export function usRegionName(code: string): string {
  return US_STATES[code]?.name || US_TERRITORIES[code] || code;
}

/** Neighbors used for distance/contiguity: land neighbors plus the AK/HI Pacific fallback. */
export function effectiveNeighbors(code: string): string[] {
  const st = US_STATES[code];
  if (!st) return [];
  const extra = PACIFIC_FALLBACK[code] || [];
  // WA/OR/CA see AK/HI as reachable too, so the relation stays symmetric.
  const reverse = Object.entries(PACIFIC_FALLBACK)
    .filter(([, list]) => list.includes(code))
    .map(([k]) => k);
  return Array.from(new Set([...st.neighbors, ...extra, ...reverse]));
}

/**
 * Hop distance between two states over effectiveNeighbors (BFS). Same state → 0.
 * Unknown codes → Infinity.
 */
const distanceCache = new Map<string, number>();
export function stateHopDistance(a: string, b: string): number {
  if (a === b) return isUsState(a) ? 0 : Infinity;
  if (!isUsState(a) || !isUsState(b)) return Infinity;
  const key = a < b ? `${a}|${b}` : `${b}|${a}`;
  const cached = distanceCache.get(key);
  if (cached !== undefined) return cached;
  const seen = new Set<string>([a]);
  let frontier = [a];
  let depth = 0;
  let found = Infinity;
  while (frontier.length > 0 && found === Infinity) {
    depth++;
    const next: string[] = [];
    for (const cur of frontier) {
      for (const n of effectiveNeighbors(cur)) {
        if (seen.has(n)) continue;
        if (n === b) {
          found = depth;
          break;
        }
        seen.add(n);
        next.push(n);
      }
      if (found !== Infinity) break;
    }
    frontier = next;
  }
  distanceCache.set(key, found);
  return found;
}
